import { useMemo } from 'react';
import { Card, Heading, Inline, Notice, Stack } from '@etg/wings';
import { DangerousHtmlContent, Pictogram } from '@eti/components';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { SELF_TRANSFER } from '../../../../../constants/eventSegmentTypes';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';

const stackStyles = css`
  flex: 1;
`;

const BaggageDetails = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { brand } = useSiteContext();
  const { bounds } = useOrderDetails();

  const selfTransferGuaranteeThresholdInMilliseconds =
    60 * 1000 * (p('IbeClient.ShowNoSelfTransferGuaranteeInfo.MinLayoverMinutes') || 0);

  const hasSelfTransferDurationLessThanThreshold = useMemo(
    () =>
      bounds.some((bound) =>
        bound.segments.some(
          (segment) =>
            segment.__typename === 'EventSegment' &&
            segment.duration < selfTransferGuaranteeThresholdInMilliseconds &&
            segment.types.some((type) => type === SELF_TRANSFER),
        ),
      ),
    [bounds, selfTransferGuaranteeThresholdInMilliseconds],
  );

  const baggageDescription = hasSelfTransferDurationLessThanThreshold
    ? t('OrderDetails.CheckIn.CheckedBaggage.SelfTransfer.Description')
    : t('OrderDetails.CheckIn.CheckedBaggage.Description');

  return (
    <Card data-testid="check-in-baggage-details">
      <Stack spacing={24}>
        <Heading as="h2" data-testid="check-in-baggage-details-title" level={3}>
          {t('OrderDetails.CheckIn.BaggageDetails.Title')}
        </Heading>
        <Inline spacing={16}>
          <Pictogram
            brand={brand.code}
            data-testid="check-in-cabin-baggage-icon"
            height={82}
            id="cabin-baggage-new"
            width={45}
          />
          <Stack className={stackStyles} spacing={8}>
            <Heading as="h3" data-testid="check-in-cabin-baggage-title" level={4}>
              {t('OrderDetails.CheckIn.CabinBaggage.Title')}
            </Heading>
            <DangerousHtmlContent content={t('OrderDetails.CheckIn.CabinBaggage.Description')} />
          </Stack>
        </Inline>
        <Inline spacing={16}>
          <Pictogram
            brand={brand.code}
            data-testid="check-in-checked-baggage-icon"
            height={76}
            id="checked-baggage-new"
            width={45}
          />
          <Stack className={stackStyles} spacing={8}>
            <Heading as="h3" data-testid="check-in-checked-baggage-title" level={4}>
              {t('OrderDetails.CheckIn.CheckedBaggage.Title')}
            </Heading>
            {hasSelfTransferDurationLessThanThreshold && (
              <Notice data-testid="check-in-baggage-self-transfer-info" variant="caution">
                {t('OrderDetails.CheckIn.CheckedBaggage.SelfTransfer.Info')}
              </Notice>
            )}
            <DangerousHtmlContent content={baggageDescription} />
          </Stack>
        </Inline>
      </Stack>
    </Card>
  );
};

export default BaggageDetails;
